import { useState, useEffect } from 'react'
import { api, formatDate, isFutureOrToday, formatDateNumerique } from '../api'
import { useAuth } from '../AuthContext'
import RinaContextCard from '../components/RinaContextCard'
import { useTheme } from '../theme'
import PageHub from '../components/PageHub'

/**
 * Activite producteur — fidele au prototype v7.2 (ActiviteView) : carte CA du mois
 * (serif + mini-graphe 6 mois), 3 tuiles (Offres actives / Inscriptions / Taux),
 * « Activite recente ». Ouvert depuis la carte « Votre activite » de l'Accueil
 * (retour ‹ Accueil gere par App) — ce n'est pas un onglet.
 */

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
const eur = (n) => Number(n || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

function ContenuActiviteProducteur() {
  const { isWeb3 } = useTheme()
  const { token } = useAuth()
  const tc = 'text-ink'
  const ts = 'text-ink/60'
  const cardBg = 'panneau-verre'

  const [stats, setStats] = useState(null)
  const [marches, setMarches] = useState([])
  const [history, setHistory] = useState([])
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!token) return
    Promise.all([
      api.producer.getStats().catch(() => null),
      api.producer.getMarches().catch(() => []),
      api.producer.getHistory().catch(() => []),
      api.producer.getInvoices().catch(() => []),
    ]).then(([s, m, h, inv]) => {
      setStats(s)
      setMarches(Array.isArray(m) ? m : [])
      setHistory(Array.isArray(h) ? h : [])
      setInvoices(Array.isArray(inv) ? inv : [])
    }).finally(() => setLoading(false))
  }, [token])

  const now = new Date()

  // CA par mois (6 derniers) — memes donnees que la carte de l'Accueil
  const caParMois = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1)
    return history
      .filter(p => p.ca != null && p.marche?.date)
      .filter(p => { const md = new Date(p.marche.date); return md.getMonth() === d.getMonth() && md.getFullYear() === d.getFullYear() })
      .reduce((s, p) => s + Number(p.ca), 0)
  })
  const caMois = caParMois[5]
  const caPrev = caParMois[4]
  const maxCa = Math.max(...caParMois, 1)
  const caGrowth = caPrev > 0 ? Math.round(((caMois - caPrev) / caPrev) * 100) : null

  // Taux de presence
  const taux = stats?.nbParticipations > 0
    ? Math.round((stats.nbMarches / stats.nbParticipations) * 100)
    : null

  // Activite recente (mix marches + inscriptions + factures, tri par date)
  const recentActivity = []
  marches.forEach(p => {
    if (p.marche && isFutureOrToday(p.marche.date)) {
      recentActivity.push({
        date: new Date(p.createdAt || p.marche.date),
        text: `Inscription — ${p.marche.lieu || p.marche.nom || 'marché'}`,
        detail: formatDate(p.marche.date),
      })
    }
  })
  history.slice(0, 5).forEach(p => {
    if (p.marche) {
      recentActivity.push({
        date: new Date(p.marche.date),
        text: `Marché terminé${p.ca > 0 ? ` — CA ${Number(p.ca).toFixed(0)}€` : ''}`,
        detail: formatDate(p.marche.date),
      })
    }
  })
  invoices.filter(i => i.statut === 'PAYEE').slice(0, 3).forEach(i => {
    recentActivity.push({
      date: new Date(i.dateEmission),
      text: `Commission réglée — ${Number(i.montantTTC).toFixed(2).replace('.', ',')}€`,
      // `dateEmission` est un JOUR calendaire (`@db.Date`) : lecture UTC.
      detail: formatDateNumerique(i.dateEmission),
    })
  })
  recentActivity.sort((a, b) => b.date - a.date)
  const activities = recentActivity.slice(0, 5)

  return (
    <>
      {(
        <RinaContextCard
          tc={tc}
         
          message="Le détail de ton activité — CA, offres actives, derniers versements."
        />
      )}

      {/* ===== Carte CA du mois (proto : serif 38 + mini-graphe pleine largeur) ===== */}
      <div className={`rounded-[26px] p-5 animate-fade-up ${cardBg}`}>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.2em]" style={{ color: 'rgb(var(--c-teal))' }}>
          Mon activité · {MOIS[now.getMonth()]} {now.getFullYear()}
        </p>
        <p className={`font-serif text-[38px] leading-none mt-2 ${tc}`}>
          {loading ? '…' : `${eur(caMois)} €`}
        </p>
        <p className={`text-[12px] mt-1 ${ts}`}>
          Chiffre d'affaires du mois
          {caGrowth !== null && (
            <span className="font-semibold" style={{ color: caGrowth >= 0 ? 'rgb(var(--c-teal))' : '#d9534f' }}>
              {' '}· {caGrowth >= 0 ? '↑' : '↓'} {Math.abs(caGrowth)}% vs {MOIS[(now.getMonth() + 11) % 12]}
            </span>
          )}
        </p>
        <div className="flex items-end gap-1.5 mt-3.5" style={{ height: 46 }}>
          {caParMois.map((v, i) => (
            <span key={i} className="flex-1 rounded-md"
              style={{ height: `${Math.max(8, (v / maxCa) * 46)}px`, background: i === 5 ? 'rgb(var(--c-primary))' : 'rgba(20,184,166,0.3)' }} />
          ))}
        </div>
      </div>

      {/* ===== 3 tuiles (proto : serif 26, teal / indigo / orange) ===== */}
      <div className="grid grid-cols-3 gap-2.5 mt-3">
        {[
          { v: loading ? '…' : (stats?.nbProduitsActifs ?? 0), l: 'Offres actives', c: 'rgb(var(--c-teal))' },
          { v: loading ? '…' : marches.length, l: 'Inscriptions', c: 'rgb(var(--c-indigo))' },
          { v: loading ? '…' : (taux !== null ? `${taux}%` : '—'), l: 'Taux', c: 'rgb(var(--c-primary))' },
        ].map((s) => (
          <div key={s.l} className={`rounded-[20px] px-2 py-4 text-center ${cardBg}`}>
            <p className="font-serif text-[26px]" style={{ color: s.c }}>{s.v}</p>
            <p className={`text-[10.5px] mt-0.5 ${ts}`}>{s.l}</p>
          </div>
        ))}
      </div>

      {/* ===== Activite recente (proto : rangees ✓ teal) ===== */}
      {activities.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] mt-5 mb-2.5 px-0.5" style={{ color: 'rgb(var(--c-teal))' }}>
            Activité récente
          </p>
          <div className="flex flex-col gap-2">
            {activities.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-[18px] px-4 py-3 animate-fade-up ${cardBg}`}
                style={{ animationDelay: `${0.06 + i * 0.04}s` }}>
                <span className="w-[30px] h-[30px] rounded-full flex-shrink-0 flex items-center justify-center text-[14px] font-extrabold"
                  style={{ background: 'rgba(20,184,166,0.16)', border: '1px solid rgba(20,184,166,0.45)', color: '#0a6b62' }}>✓</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-[13px] font-bold truncate ${tc}`}>{a.text}</p>
                  <p className={`text-[11px] ${ts}`}>{a.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PageActiviteProducteur() {
  return <PageHub retour="home"><ContenuActiviteProducteur /></PageHub>
}

